import { format } from "d3-format";
import sortBy from "lodash-es/sortBy";
import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { surface, fontWeight, fontSize, spacing, zIndex, dataViz, borderRadius } from "@atlas-int/tokens";
import { useTableSort } from "@atlas-int/ui";
import GlossaryTerm, { TooltipAlign, TooltipType } from "./GlossaryTerm";
import { ArrowIconContainer } from "./Utils";
import usePromotableZIndex from "./usePromotableZIndex";
import Diamond, { Fill } from "./Diamond";
import StarRating, { DecileClassification } from "./StarRating";
import TitleCell, { SortCriterion, SortDirection } from "./TitleCell";
import { CPTradeDirection, CPTradeFlow, CPProductClass, CPProductLevel, QueryLevel, NodeSizing } from "@atlas-int/data/types";

// Taken from https://stackoverflow.com/a/22885197:
const log10 = Math.log(10);
const getSignificantDigitCount = (n: number) => {
  n = Math.abs(+String(n).replace(".", "")); //remove decimal and make positive
  if (n === 0) {
    return 0;
  }
  while (n !== 0 && n % 10 === 0) {
    n /= 10; //kill the 0s at the end of n
  }

  return Math.floor(Math.log(n) / log10) + 1; //get number of digits
};
// Format monetary sums into nice number with as many significant digits as in
// the input not exceeding 3. Also replace the `G` prefix with `B` because `B`
// makes more sense for money sums. The other prefixes are fine:
export const formatTradeValue = (input: number) => {
  const maxNumSignificantDigits = 3;
  const numSignificantDigitsInInput = getSignificantDigitCount(input);
  const numSignificantDigitsInOutput =
    numSignificantDigitsInInput <= maxNumSignificantDigits
      ? numSignificantDigitsInInput
      : maxNumSignificantDigits;
  return format(`$.${numSignificantDigitsInOutput}s`)(input).replace("G", "B");
};

export const formatTradeValueUSD = (input: number, getFluentString: (id: string, args?: object) => string) => {
  return (
    getFluentString("trade-format-value-usd") + " " + formatTradeValue(input)
  );
};

// ProductClass alias for backward compatibility within this file
type ProductClass = CPProductClass;
const ProductClass = CPProductClass;

export interface QueryStore {
  country: number | undefined;
  queryLevel: QueryLevel;
  id: number | undefined;
  product: number | undefined;
  year: number;
  startYear: number | undefined;
  tradeDirection: CPTradeDirection;
  productClass: ProductClass;
  nodeSizing: NodeSizing;
  tradeFlow: CPTradeFlow;
  target: Target;
  partner: number | undefined;
}

// "product" vs "partner" in graph control panel:
export enum Target {
  Product = "Product",
  Partner = "Partner",
}

// Re-export NodeSizing from @atlas-int/data (canonical source)
export { NodeSizing } from "@atlas-int/data/types";

// Errors out at compile time if a discriminating `switch` doesn't catch all cases
// of an enum and at run time if for some reason an invalid enum value is passed.
// See https://basarat.gitbooks.io/typescript/content/docs/types/discriminated-unions.html
export function failIfValidOrNonExhaustive(
  _variable: never,
  message: string,
): never {
  throw new Error(message);
}

export const nonBreakingWhiteSpaceCharacter = "\u00a0";
// ??? WHAT IS THIS FOR??
export const newGetDisplayedProductCode = (
  code: string,
  productClass: ProductClass,
  _level: CPProductLevel,
) => {
  // const productClassPhrase = (productClass === ProductClass.HS) ?
  //                             __lexiconText('applicationWide.productClass.HS') :
  //                             __lexiconText('applicationWide.productClass.SITC');
  // const productClassPhrase =
  //   productClass === ProductClass.HS
  //     ? __lexiconText("applicationWide.productClassWithVersion.HS")
  //     : __lexiconText("applicationWide.productClassWithVersion.SITC");

  // Make the space between product class and product code non-breaking:
  // return `${code} ${productClassPhrase}${newCPProductLevelToDigit(level)}`.replace(/ /g, nonBreakingWhiteSpaceCharacter);
  return `${code} ${productClass}`.replace(
    / /g,
    nonBreakingWhiteSpaceCharacter,
  );
};

// ____
const titleCellBaseZIndex = zIndex.tooltip;
const promotedTitleCellZIndex = zIndex.overlay;
const tooltipZIndex = zIndex.glossaryOverlay; // Higher than all other z-indices to ensure tooltips are always on top

const zIndices = {
  tableCellProductName: titleCellBaseZIndex + 5,
  tableCellComplexity: titleCellBaseZIndex,
  tableCellOpportunityGain: titleCellBaseZIndex,
  tableCellFeasibility: titleCellBaseZIndex,
  tableCellExportValue: titleCellBaseZIndex,
  tableCellExportValueChange: titleCellBaseZIndex,
  dataCell: 10,
  tooltip: tooltipZIndex,
};

//#region Styling
const strokeColor = dataViz.strokeGray;
const strokeLightColor = dataViz.strokeLightGray;
const productTextColor = dataViz.textSecondary;
const Root = styled.div<{ condensed?: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.condensed
      ? "3fr repeat(2, 1.5fr)"
      : " 3fr repeat(2, 1.5fr) repeat(2, 1fr) 1.5fr"};
  overflow: auto;
  grid-auto-rows: max-content;

  /* stylelint-disable-next-line property-no-vendor-prefix */
  ::-webkit-scrollbar {
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-appearance: none;
    height: 8px;
    width: 8px;
  }
  /* stylelint-disable-next-line property-no-vendor-prefix */
  ::-webkit-scrollbar-thumb {
    border-radius: ${borderRadius.base};
    background-color: ${dataViz.overlay};
  }
  /* stylelint-disable-next-line property-no-vendor-prefix */
  ::-webkit-scrollbar-track {
    background-color: ${dataViz.overlayLight};
  }

  @media (max-width: 1050px) {
    font-size: ${fontSize.sm};
  }
`;
const cellBottomBorderStyle = css`
  border-bottom: 1px solid ${strokeLightColor};
`;

const DataCell = styled.div`
  ${cellBottomBorderStyle}
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: ${zIndices.dataCell};
`;
const titleColoredBarSeparationFromEdge = "0.5rem";
const ProductNameOuterContainer = styled(DataCell)`
  position: sticky;
  left: 0;
  background-color: ${surface.white};
  z-index: 15;
  justify-content: flex-start;
  border-right: 1px solid ${strokeColor};

  &::before {
    content: "";
    display: block;
    position: absolute;
    top: ${titleColoredBarSeparationFromEdge};
    bottom: ${titleColoredBarSeparationFromEdge};
    left: 0;
    width: 0.5rem;
    background-color: var(--colored-bar-background-color);
  }

  svg {
    display: none;
  }

  &:hover {
    background-color: ${dataViz.tableRowHover};

    svg {
      display: block;
    }
  }
`;
const ExportValueChangeContainer = styled.div`
  margin-left: ${spacing[4]};
`;
const ProductName = styled.span`
  color: ${productTextColor};

  &::after {
    content: "";
    display: inline-block;
    margin-right: 0.5rem;
  }
`;
const ProductCode = styled.span`
  color: ${productTextColor};
  font-weight: ${fontWeight.light};
`;
const ArrowDown = ArrowIconContainer;
const ArrowUp = styled(ArrowIconContainer)`
  transform: rotate(180deg);
`;

const ProductLink = styled.div`
  display: block;
  padding: ${spacing[8]};
  width: 100%;

  @media (max-width: 975px) {
    padding: ${spacing[4]} ${spacing[4]} ${spacing[4]} ${spacing[8]};
  }
`;
const DistanceLegendContainer = styled.div`
  margin-top: ${spacing[4]};
`;
const DistanceLegend = styled.div`
  display: flex;
  position: relative;
  align-items: center;

  &:first-child {
    margin-bottom: 0.3rem;
  }
`;
const LegendLabel = styled.em`
  margin-left: ${fontSize.lg};
  font-size: ${fontSize.base};

  @media (max-width: 1050px) {
    margin-left: 0.5rem;
    font-size: ${fontSize.sm};
  }
`;
//#endregion

// Allow CSS custom properties
declare module "csstype" {
  interface Properties {
    "--colored-bar-background-color"?: string;
  }
}

const formatChange = format(".1~%");
const TableRow = (props: {
  product: TableDatum;
  year: number;
  condensed: boolean;
  highlight: Function;
  setRingItem: Function;
}) => {
  const { product, condensed, highlight, setRingItem } = props;
  const {
    id,
    color,
    name,
    code,
    complexityDecile,
    opportunityGainDecile,
    feasibilityDecile,
    globalExportValue,
    globalExportValueChange,
  } = product;
  const productNameStyle: React.CSSProperties = {
    "--colored-bar-background-color": color,
  };
  const productCode = `(${newGetDisplayedProductCode(code, CPProductClass.HS, CPProductLevel.fourDigit)})`;
  const globalExportValueChangeMagnitude = Math.abs(globalExportValueChange);
  const arrowColor = globalExportValueChange >= 0 ? dataViz.arrowPositive : dataViz.arrowNegative;
  const Arrow = globalExportValueChange >= 0 ? ArrowUp : ArrowDown;

  const mouseEnter = () => highlight(id);
  const mouseOut = () => highlight(null);
  const click = () => setRingItem(id);
  return (
    <div role="row" style={{ display: 'contents' }}>
      <ProductNameOuterContainer
        role="cell"
        onMouseEnter={mouseEnter}
        onMouseLeave={mouseOut}
        onClick={click}
        style={productNameStyle}
      >
        <ProductLink>
          <ProductName>{name}</ProductName>
          <ProductCode>{productCode}</ProductCode>
        </ProductLink>
      </ProductNameOuterContainer>
      <DataCell
        role="cell"
        onMouseEnter={mouseEnter}
        onMouseLeave={mouseOut}
        onClick={click}
      >
        <StarRating decile={feasibilityDecile} />
      </DataCell>
      {!condensed && (
        <DataCell role="cell">
          <StarRating decile={opportunityGainDecile} />
        </DataCell>
      )}
      <DataCell
        role="cell"
        onMouseEnter={mouseEnter}
        onMouseLeave={mouseOut}
        onClick={click}
      >
        <StarRating decile={complexityDecile} />
      </DataCell>
      {!condensed && <DataCell role="cell">{formatTradeValue(globalExportValue)}</DataCell>}
      {!condensed && (
        <DataCell role="cell">
          <Arrow
            color={arrowColor}
            height={20}
          />
          {!condensed && (
            <ExportValueChangeContainer>
              {formatChange(globalExportValueChangeMagnitude)}
            </ExportValueChangeContainer>
          )}
        </DataCell>
      )}
    </div>
  );
};

export interface TableDatum {
  normalizedDistance: number;
  exportRca: number;
  normalizedCog: number;
  id: string;
  color: string;
  name: string;
  code: string;
  complexityDecile: DecileClassification;
  rcaDecile: DecileClassification;
  opportunityGainDecile: DecileClassification;
  feasibilityDecile: DecileClassification;
  globalExportValue: number;
  globalExportValueChange: number;
  normalizedPci: number;
  normalizedFeasibility: number;
  normalizedOpportunityGain: number;
  score: number;
}

interface Props {
  products: TableDatum[];
  getFluentString: (id: string, args?: object) => string;
  width: number;
  height: number;
  zIndexFromParent: number;
  year: number;
  condensed?: boolean;
  highlight?: (id: string | null) => void;
  setRingItem?: (id: string) => void;
}

// Map string sort keys to SortCriterion enum values
type SortKey = "Complexity" | "OpportunityGain" | "Feasibility" | "ExportValue" | "ExportValueChange" | "RCA";
const sortKeyToCriterion: Record<SortKey, SortCriterion> = {
  Complexity: SortCriterion.Complexity,
  OpportunityGain: SortCriterion.OpportunityGain,
  Feasibility: SortCriterion.Feasibility,
  ExportValue: SortCriterion.ExportValue,
  ExportValueChange: SortCriterion.ExportValueChange,
  RCA: SortCriterion.RCA,
};
const criterionToSortKey: Record<SortCriterion, SortKey> = {
  [SortCriterion.Complexity]: "Complexity",
  [SortCriterion.OpportunityGain]: "OpportunityGain",
  [SortCriterion.Feasibility]: "Feasibility",
  [SortCriterion.ExportValue]: "ExportValue",
  [SortCriterion.ExportValueChange]: "ExportValueChange",
  [SortCriterion.RCA]: "RCA",
};

const Table = (props: Props) => {
  const {
    products,
    width = 1000,
    height = 600,
    zIndexFromParent = 10,
    year = 2021,
    condensed = false,
    highlight = () => {},
    setRingItem = () => {},
  } = props;

  const { sortColumn, sortDirection: sortDir, toggleSort } = useTableSort<SortKey>({
    defaultColumn: "Complexity",
    defaultDirection: "desc",
  });
  const sortCriterion = sortKeyToCriterion[sortColumn];
  const sortDirection = sortDir === "desc" ? SortDirection.Descending : SortDirection.Ascending;

  const { promoteZIndex, restoreZIndex, getZIndex } = usePromotableZIndex({
    normalZIndices: zIndices,
    promotedZIndex: promotedTitleCellZIndex,
  });

  let productsSortedAscendingly: typeof products;
  if (sortCriterion === SortCriterion.Complexity) {
    productsSortedAscendingly = sortBy(products, [
      (product) => product.normalizedPci,
    ]);
  } else if (sortCriterion === SortCriterion.OpportunityGain) {
    productsSortedAscendingly = sortBy(products, [
      (product) => product.normalizedCog,
    ]);
  } else if (sortCriterion === SortCriterion.Feasibility) {
    productsSortedAscendingly = sortBy(products, [
      (product) => product.normalizedDistance,
    ]);
  } else if (sortCriterion === SortCriterion.ExportValue) {
    productsSortedAscendingly = sortBy(products, [
      (product) => product.globalExportValue,
    ]);
  } else if (sortCriterion === SortCriterion.ExportValueChange) {
    productsSortedAscendingly = sortBy(products, [
      (product) => product.globalExportValueChange,
    ]);
  } else if (sortCriterion === SortCriterion.RCA) {
    productsSortedAscendingly = sortBy(products, [
      (product) => product.exportRca,
    ]);
  } else {
    failIfValidOrNonExhaustive(
      sortCriterion,
      "Invalid sort criterion " + sortCriterion,
    );
    productsSortedAscendingly = products;
  }

  let sortedProducts: typeof products;
  if (sortDirection === SortDirection.Ascending) {
    sortedProducts = productsSortedAscendingly;
  } else if (sortDirection === SortDirection.Descending) {
    sortedProducts = productsSortedAscendingly.reverse();
  } else {
    failIfValidOrNonExhaustive(
      sortDirection,
      "Invalid sort direction" + sortDirection,
    );
    sortedProducts = productsSortedAscendingly;
  }

  const onColumnTitleClick = (nextCriterion: SortCriterion) => {
    toggleSort(criterionToSortKey[nextCriterion]);
  };

  const distanceTooltip = (
    <DistanceLegendContainer>
      <DistanceLegend>
        <Diamond fill={Fill.Full} />
        <Diamond fill={Fill.Full} />
        <Diamond fill={Fill.Full} />
        <Diamond fill={Fill.Full} />
        <Diamond fill={Fill.Full} />
        <LegendLabel>Opportunity Gain</LegendLabel>
      </DistanceLegend>
      <DistanceLegend>
        <Diamond fill={Fill.None} />
        <Diamond fill={Fill.None} />
        <Diamond fill={Fill.None} />
        <Diamond fill={Fill.None} />
        <Diamond fill={Fill.None} />
        <LegendLabel>most distant</LegendLabel>
      </DistanceLegend>
    </DistanceLegendContainer>
  );

  const titleRow = (
    <>
      <TitleCell
        hasAssignedSortCriterion={false}
        zIndex={getZIndex("tableCellProductName")}
        stickColumnToLeft={true}
      >
        Product Name
        {/* {getFluentString("high-potential-products-table-name-column-title")} */}
      </TitleCell>
      <TitleCell
        hasAssignedSortCriterion={true}
        assignedSortCriterion={SortCriterion.Feasibility}
        onClick={onColumnTitleClick}
        sortCriterion={sortCriterion}
        sortDirection={sortDirection}
        zIndex={getZIndex("tableCellFeasibility")}
        promoteZIndex={() => promoteZIndex("tableCellFeasibility")}
        restoreZIndex={restoreZIndex}
      >
        <div>
          <GlossaryTerm
            type={TooltipType.Standard}
            term={`"Nearby" Distance`}
            explanation={
              "A country's ability to enter in to a new product, measured from 0 to 1. A 'nearby' product (closer to 0) requires related capabilities to existing products, offering a greater likelihood of success."
            }
            citation={undefined}
            align={TooltipAlign.Standard}
            attachment={distanceTooltip}
          />
        </div>
      </TitleCell>
      {!condensed && (
        <TitleCell
          hasAssignedSortCriterion={true}
          assignedSortCriterion={SortCriterion.OpportunityGain}
          onClick={onColumnTitleClick}
          sortCriterion={sortCriterion}
          sortDirection={sortDirection}
          zIndex={getZIndex("tableCellOpportunityGain")}
          promoteZIndex={() => promoteZIndex("tableCellOpportunityGain")}
          restoreZIndex={restoreZIndex}
        >
          <div>
            <GlossaryTerm
              type={TooltipType.Standard}
              term={"Opportunity Gain"}
              explanation={
                "Measures opportunities for future diversification in entering a product, by opening new links to complex products"
              }
              citation={undefined}
              align={TooltipAlign.Standard}
              attachment={null}
            />
          </div>
        </TitleCell>
      )}
      <TitleCell
        hasAssignedSortCriterion={true}
        assignedSortCriterion={SortCriterion.Complexity}
        sortCriterion={sortCriterion}
        sortDirection={sortDirection}
        onClick={onColumnTitleClick}
        zIndex={getZIndex("tableCellComplexity")}
        promoteZIndex={() => promoteZIndex("tableCellComplexity")}
        restoreZIndex={restoreZIndex}
      >
        <div>
          <GlossaryTerm
            type={TooltipType.Standard}
            term={"Product Complexity"}
            explanation={
              "Measures the amount of diversity of knowhow required to make a product."
            }
            citation={undefined}
            align={TooltipAlign.Standard}
            attachment={null}
          />
        </div>
      </TitleCell>
      {!condensed && (
        <TitleCell
          hasAssignedSortCriterion={true}
          assignedSortCriterion={SortCriterion.ExportValue}
          onClick={onColumnTitleClick}
          sortCriterion={sortCriterion}
          sortDirection={sortDirection}
          zIndex={getZIndex("tableCellExportValue")}
          promoteZIndex={() => promoteZIndex("tableCellExportValue")}
          restoreZIndex={restoreZIndex}
        >
          Global size (USD)
          {/* {getFluentString("high-potential-products-table-trade-column-title")} */}
        </TitleCell>
      )}
      {!condensed && (
        <TitleCell
          hasAssignedSortCriterion={true}
          assignedSortCriterion={SortCriterion.ExportValueChange}
          onClick={onColumnTitleClick}
          sortCriterion={sortCriterion}
          sortDirection={sortDirection}
          zIndex={getZIndex("tableCellExportValueChange")}
          promoteZIndex={() => promoteZIndex("tableCellExportValueChange")}
          restoreZIndex={restoreZIndex}
        >
          Global growth 5 YR
          {
            // getFluentString(
            //   "high-potential-products-table-trade-change-column-title",
            // )
          }
        </TitleCell>
      )}
    </>
  );

  const contentRows = sortedProducts.map((product) => (
    <TableRow
      product={product}
      year={year}
      key={product.id}
      condensed={condensed}
      highlight={highlight}
      setRingItem={setRingItem}
    />
  ));
  return (
    <>
      <div id="overlay-portal-container" />
      <Root
        role="table"
        condensed={condensed}
        style={{
          width,
          height,
          zIndex: zIndexFromParent,
        }}
      >
        <div role="row" style={{ display: 'contents' }}>
          {titleRow}
        </div>
        {contentRows}
      </Root>
    </>
  );
};

export default Table;
